
/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */
import {
    Checkbox,
    CrfCondition,
    DatePicker,
    Field,
    FormInputOptionsContext,
    GroupedFieldErrorContext,
    GroupedFieldErrorDisplay,
    GroupedFieldErrorFlag,
    GroupedFields,
    ICrfConditionParameters,
    IForm,
    IFormInputOptionsContext,
    Input,
    InputOptionMode
} from '@ngt/opms';
import { ReactElement, useMemo, useState } from 'react';
/*
 * ----------------------------------------------------------------------------------
 * Imports - Internal
 * ----------------------------------------------------------------------------------
 */
import { Box, Grid2 } from '@mui/material';
import { ConditionMode, FormErrorBoundary, FormErrorBoundaryMode } from '@ngt/forms-core';


const dateInputOptionsContext: IFormInputOptionsContext = {
    labelMode: InputOptionMode.Hide,
    errorMode: InputOptionMode.Hide
}
const checkboxInputOptionsContext: IFormInputOptionsContext = {
    labelMode: InputOptionMode.UseCustom,
    errorMode: InputOptionMode.Hide
}

export interface IDateAndCheckboxFieldProps<TForm extends IForm = IForm> {
    label: ReactElement;
    datePropertyPath: string;
    notDonePropertyPath: string;
    condition: (params: ICrfConditionParameters<TForm>) => boolean;
    checkboxLabel?: string
}

const DateAndCheckboxField = <TForm extends IForm = IForm>({ 
    label,
    datePropertyPath,
    notDonePropertyPath,
    condition,
    checkboxLabel
}: IDateAndCheckboxFieldProps<TForm>) => {

    const [expanded, setExpanded] = useState<boolean>(true);

    const errorContext = useMemo(() => {

        return {
            expanded,
            setExpanded
        }
    }, [expanded, setExpanded]);
    
    return (
        <GroupedFieldErrorContext.Provider value={errorContext}>
            <FormErrorBoundary mode={FormErrorBoundaryMode.Contain}>
                <GroupedFields>
                    <Grid2
                        container
                        spacing={2}
                        alignItems="center"
                        sx={{
                            p:2
                        }}
                    >
                        <Grid2
                            size={{
                                xl: 7,
                                lg: 7,
                                md: 6,
                                sm: 12,
                                xs: 12
                            }}
                        >
                            {label}
                        </Grid2>
                        <Grid2
                            size={{
                                xl: 3,
                                lg: 3,
                                md: 3,
                                sm: 6,
                                xs: 6
                            }}
                        >
                            <CrfCondition
                                mode={ConditionMode.Enable}
                                condition={condition}
                                subscription={{ value: true }}
                            >
                                <Field propertyPath={datePropertyPath}>
                                    <Box
                                        sx={{
                                            display: 'flex'
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                flex: '1 1 auto'
                                            }}
                                        >
                                            <FormInputOptionsContext.Provider value={dateInputOptionsContext}>
                                                <Input 
                                                    component={DatePicker}
                                                />
                                            </FormInputOptionsContext.Provider>
                                        </Box>
                                        <Box
                                            sx={{
                                                flex: '0 0 auto',
                                                minWidth: 32,
                                                maxWidth: 32
                                            }}
                                        >
                                            <GroupedFieldErrorFlag />
                                        </Box>
                                    </Box>
                                </Field>
                            </CrfCondition>
                        </Grid2>
                        <Grid2
                            size={{
                                xl: 2,
                                lg: 2,
                                md: 3,
                                sm: 6,
                                xs: 6
                            }}
                        >
                            <Field propertyPath={notDonePropertyPath}>
                                <Box
                                    sx={{
                                        display: 'flex'
                                    }}
                                >
                                    <Box
                                        sx={{
                                            flex: '1 1 auto'
                                        }}
                                    >
                                        <FormInputOptionsContext.Provider value={checkboxInputOptionsContext}>
                                            <Input 
                                                component={Checkbox}
                                                label={checkboxLabel ?? 'Not Done'}
                                            />
                                        </FormInputOptionsContext.Provider>
                                    </Box>
                                    <Box
                                        sx={{
                                            flex: '0 0 auto',
                                            minWidth: 32,
                                            maxWidth: 32
                                        }}
                                    >
                                        <GroupedFieldErrorFlag />
                                    </Box>
                                </Box>
                            </Field>
                        </Grid2>
                    </Grid2>
                    <GroupedFieldErrorDisplay />
                </GroupedFields>
            </FormErrorBoundary>
        </GroupedFieldErrorContext.Provider>
    );
}

export default DateAndCheckboxField;